import { useState } from 'react'
import { uiActions } from '@/core/state/ui'
import { formatCardValue } from '@/features/summary-statistics/cardDisplay'
import type { StatCard } from '@/features/summary-statistics/cards'
// Aliased away from `use*`: it is a Vue composable over module-level refs, not
// a React hook, and calling it at module scope is correct — under its own name
// react-hooks/rules-of-hooks would (reasonably) refuse it.
import { useGettingStarted as gettingStartedChecklist } from '@/features/onboarding/composables/useGettingStarted'
import { useStatCards } from '@/react/hooks/useStatCards'
import { StatCardDetail } from './StatCardDetail'
import './statcards.css'

/**
 * The stat cards (Phase 1I), replacing
 * `features/summary-statistics/components/SummaryStatisticsBlocks.vue` and the
 * `statCards` island around it.
 *
 * The DOM contract is load-bearing and reproduced verbatim: `stat-bar` on the
 * row with `data-tour="cost-card"` (the guided tour's second step),
 * `stat-card-<id>` per card, `stat-card-controls` ABSENT — not hidden — in
 * presentation mode, `.stat-value` containing only the formatted value, and
 * `.summary-statistics-blocks` on the root, which the bar's stylesheet targets.
 */

/**
 * Where each card's click goes.
 *
 * These were `click:*` emits the island forwarded to the UI store; with the
 * island gone they call the store directly. Cost of Averaging is the odd one:
 * it opens the config that PRODUCES the number rather than a breakdown, because
 * there is nothing to break down until a design run has happened — and that is
 * the right destination either way, to run it or to change it and re-run.
 * Everything not listed falls through to this component's own detail dialog.
 */
const ROUTES: Record<string, () => void> = {
  networkRisk: () => uiActions.openRiskAnalysis(),
  dataQuality: () => uiActions.openDataValidation(),
  totalCost: () => uiActions.openTotalCostAnalysis(),
  transportCost: () => uiActions.openShippingCostAnalysis(),
  fixedCost: () => uiActions.openFixedCostAnalysis(),
  variableCost: () => uiActions.openVariableCostAnalysis(),
  capacityUtil: () => uiActions.openCapacityUtilizationAnalysis(),
  totalDemand: () => uiActions.openDemandAnalysis(),
  valueOfStochasticSolution: () => uiActions.openConfigModal('simulation', 'uncertainty'),
}

// The (still Vue) checklist sees the tick because it reads the same refs.
const { markDone: markGettingStartedDone } = gettingStartedChecklist()

export function StatCards() {
  const {
    cards, chartData, demandDisplay, presentationMode, comparisonMode, showDemandUOM,
    baseUnit, numberFormat, isLoading, costsUnavailable, retryCosts, hideCard, comparison,
  } = useStatCards()
  const [detailCard, setDetailCard] = useState<StatCard | null>(null)

  const openCard = (card: StatCard) => {
    // Before the branches: every one of them opens *some* breakdown, which is
    // what the checklist asks the user to discover, whichever card they clicked.
    markGettingStartedDone('open_stat_card')
    const route = ROUTES[card.id]
    if (route) return route()
    setDetailCard(card)
  }

  const delta = comparison.delta
  const showDelta = comparisonMode && delta !== null
  const showNote = comparisonMode && delta === null && !costsUnavailable

  return (
    <div className="summary-statistics-blocks">
      <div className="stats-blocks-inner">
        {costsUnavailable && (
          // The cards below render em dashes; this says why, and offers the way
          // out. Shown in presentation mode too: a silent row of dashes is worse
          // on a screen being presented, not better.
          <div data-testid="stat-costs-error" className="stat-costs-error">
            <i className="mdi mdi-alert-circle-outline" aria-hidden="true" />
            <span>Costs unavailable</span>
            <button
              type="button"
              data-testid="stat-costs-retry"
              className="stat-costs-retry"
              onClick={() => retryCosts()}
            >
              Retry
            </button>
          </div>
        )}

        {isLoading ? (
          <div className="stats-loading">Loading statistics...</div>
        ) : (
          <div data-tour="cost-card" data-testid="stat-bar" className="stats-container">
            {cards.length === 0 && <div className="stats-empty">No statistics cards configured</div>}
            {cards.map((card, index) => (
              <div
                key={card.id}
                className={`stat-wrapper${card.id === 'totalCost' ? ' hero-wrapper' : ''}`}
                data-card-id={card.id}
                data-card-index={index}
              >
                <div
                  data-testid={`stat-card-${card.id}`}
                  className={`stat-card clickable-card${card.id === 'totalCost' ? ' hero-card' : ''}`}
                  role="button"
                  tabIndex={0}
                  onClick={() => openCard(card)}
                  onKeyDown={(event) => {
                    if (event.key === 'Enter' || event.key === ' ') {
                      event.preventDefault()
                      openCard(card)
                    }
                  }}
                >
                  {!presentationMode && (
                    <div data-testid="stat-card-controls" className="stat-card-controls">
                      <button
                        type="button"
                        className="stat-card-close"
                        aria-label={`Hide ${card.label}`}
                        onClick={(event) => {
                          event.stopPropagation()
                          hideCard(card.id)
                        }}
                      >
                        <i className="mdi mdi-close" aria-hidden="true" />
                      </button>
                    </div>
                  )}

                  <i className={`mdi ${card.icon} stat-card-icon`} aria-hidden="true" />

                  <div className="stat-value-container">
                    {/* Only the formatted value: the E2E suite reads this
                        element's text as the number the user sees. */}
                    <div className={`stat-value${card.id === 'totalCost' ? ' hero-value' : ''}`}>
                      {formatCardValue(card, numberFormat)}
                    </div>

                    {card.id === 'totalCost' && showNote && (
                      // Comparison is on but there is nothing to say. Silence
                      // reads as a broken switch, so it says which reason applies.
                      <div className="cost-delta-compact cost-delta-positioned cost-delta-note">
                        <span className="delta-subtitle">{comparison.note}</span>
                      </div>
                    )}

                    {card.id === 'totalCost' && showDelta && (
                      <div className="cost-delta-compact cost-delta-positioned">
                        {/* One sign for the dollars, the percent, the colour and
                            the arrow: down and green is cheaper than the
                            baseline, up and red is dearer, no change is neither. */}
                        <div className="delta-header-compact">
                          <i className={`mdi mdi-delta delta-${delta.direction}`} aria-hidden="true" />
                          <span className="delta-subtitle">from baseline</span>
                        </div>

                        <div className="delta-divider" />

                        {/* `savings-text` / `increase-text` are the classes the
                            stylesheet and the E2E suite both know. */}
                        <div
                          className={`delta-value delta-${delta.direction}${
                            delta.direction === 'down'
                              ? ' savings-text'
                              : delta.direction === 'up'
                                ? ' increase-text'
                                : ''
                          }`}
                        >
                          {comparison.formattedDelta} ({comparison.formattedPercent})
                          {delta.direction !== 'none' && (
                            <i
                              className={`mdi ${delta.direction === 'down' ? 'mdi-arrow-down' : 'mdi-arrow-up'}`}
                              aria-hidden="true"
                            />
                          )}
                        </div>
                      </div>
                    )}
                  </div>

                  <div className="stat-label">{card.label}</div>

                  {card.subtitle && !presentationMode && (
                    <div className="stat-subtitle">{card.subtitle}</div>
                  )}

                  {card.id === 'totalDemand' && showDemandUOM
                    && demandDisplay.unit && demandDisplay.unit !== baseUnit && (
                    // The unit the NUMBER is in, shown only when it is not the
                    // base unit the data is stored in.
                    <div className="stat-uom-label">{demandDisplay.unit}</div>
                  )}
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {detailCard && (
        <StatCardDetail card={detailCard} chartData={chartData} onClose={() => setDetailCard(null)} />
      )}
    </div>
  )
}
